import { AxiosRequestConfig } from 'axios';

import client from 'api/client';
// Shared in-flight dedupe for GET /completed-forms (avoids double network hits
// under Strict Mode remounts / concurrent subscribers).
import { getCompletedForms as getCompletedFormsDeduped } from './completed-forms';
import { toPurposeServerValue } from 'helpers/purpose';
import { omitHasSpouse } from 'utils/hasSpouse';
import { getActingProfileUserId, isActingAsSpouseProfile } from 'utils/selectedAccount';

/* ─── Completed Forms ─── */

export function getCompletedForms(userId: number, _config?: AxiosRequestConfig) {
    return getCompletedFormsDeduped(userId);
}

export function updateCompletedForms(
    userId: number,
    payload: { [key: string]: boolean },
    config?: AxiosRequestConfig
) {
    const actingId = getActingProfileUserId() ?? userId;
    return client.patch(`/candidate_info/${actingId}/completed-forms`, payload, config);
}

/* ─── Purpose ─── */

export function getPurpose(config?: AxiosRequestConfig) {
    return client.get(candidateInfoUrl('Purpose'), config);
}

/* ─── Personal Info ─── */

export function getPersonalInfo(config?: AxiosRequestConfig) {
    return client.get(candidateInfoUrl('User'), config);
}

/* ─── Citizenship ─── */

export function getCitizenship(config?: AxiosRequestConfig) {
    return client.get(candidateInfoUrl('Citizenship'), config);
}

/* ─── Additional Details ─── */

export function getAdditionalDetails(config?: AxiosRequestConfig) {
    return client.get(candidateInfoUrl('AdditionalDetails'), config);
}

/* ─── Previous Visa ─── */

export function getPreviousVisaDetails(config?: AxiosRequestConfig) {
    return client.get(candidateInfoUrl('PreviousVisa'), config);
}

const candidateInfoUrl = (tableName: string, userId?: number | null) => {
    const resolved = userId ?? getActingProfileUserId();
    const hasUserId = typeof resolved === 'number' && Number.isFinite(resolved) && resolved > 0;
    return hasUserId ? `/candidate_info/${tableName}/${resolved}` : `/candidate_info/${tableName}`;
};

/* ─── Spouse ─── */

export function getSpouseInfo(config?: AxiosRequestConfig) {
    return client.get('/candidate_info/Spouse', config);
}

export function getProfileTable(
    tableName: string,
    userId?: number | null,
    config?: AxiosRequestConfig
) {
    return client.get(candidateInfoUrl(tableName, userId), config);
}

export function saveProfileTable(
    tableName: string,
    payload: unknown,
    userId?: number | null,
    config?: AxiosRequestConfig
) {
    return client.post(candidateInfoUrl(tableName, userId), { data: payload }, config);
}

/* ─── Family Members ─── */

export function getFamilyMembers(config?: AxiosRequestConfig) {
    return client.get(candidateInfoUrl('FamilyMember'), config);
}

export function getChildren(config?: AxiosRequestConfig) {
    return client.get(candidateInfoUrl('Child'), config);
}

/* ─── Language ─── */

export function getLanguageProficiency(config?: AxiosRequestConfig) {
    return client.get(candidateInfoUrl('Language'), config);
}

export function getSpouseLanguageProficiency(config?: AxiosRequestConfig) {
    return client.get('/candidate_info/SpouseLanguage', config);
}

/* ─── Education ─── */

export function getEducation(config?: AxiosRequestConfig) {
    return client.get(candidateInfoUrl('EducationNew'), config);
}

export function getSpouseEducation(config?: AxiosRequestConfig) {
    return client.get('/candidate_info/SpouseEducation', config);
}

/* ─── Work Experience ─── */

export function getWorkExperience(config?: AxiosRequestConfig) {
    return client.get(candidateInfoUrl('WorkExperienceNew'), config);
}

export function getSpouseWorkExperience(config?: AxiosRequestConfig) {
    return client.get('/candidate_info/SpouseWorkExperience', config);
}

/* ─── Job Offer ─── */

export function getJobOffer(config?: AxiosRequestConfig) {
    return client.get(candidateInfoUrl('JobOffer'), config);
}

/* ─── Relatives Abroad ─── */

export function getRelativesAbroad(config?: AxiosRequestConfig) {
    return client.get(candidateInfoUrl('RelativeAbroad'), config);
}

/* ─── Business ─── */

export function getBusinessOwnership(config?: AxiosRequestConfig) {
    return client.get(candidateInfoUrl('Business'), config);
}

/* ─── Save: Personal Info ─── */

export function savePersonalInfo(payload: object, config?: AxiosRequestConfig) {
    return client.post(candidateInfoUrl('User'), { data: payload }, config);
}

/* ─── Save: Purpose ─── */

export function savePurpose(purpose: string, config?: AxiosRequestConfig) {
    return client.post(
        candidateInfoUrl('Purpose'),
        { data: { purpose: toPurposeServerValue(purpose) } },
        config
    );
}

/* ─── Save: Citizenship ─── */

export function saveCitizenship(countries: string[], config?: AxiosRequestConfig) {
    return client.post(
        candidateInfoUrl('Citizenship'),
        { data: countries.map((country) => ({ country })) },
        config
    );
}

/* ─── Save: Additional Details ─── */

export function saveAdditionalDetails(payload: object, config?: AxiosRequestConfig) {
    const body = isActingAsSpouseProfile() ? omitHasSpouse(payload) : payload;
    return client.post(candidateInfoUrl('AdditionalDetails'), { data: body }, config);
}

/* ─── Save: Previous Visa ─── */

export function savePreviousVisaDetails(payload: object[], config?: AxiosRequestConfig) {
    return client.post(candidateInfoUrl('PreviousVisa'), { data: payload }, config);
}

/* ─── Save: Spouse ─── */

export function saveSpouseInfo(payload: object, config?: AxiosRequestConfig) {
    return client.post('/candidate_info/Spouse', { data: payload }, config);
}

/* ─── Save: Family Members ─── */

export function saveFamilyMembers(payload: object[], config?: AxiosRequestConfig) {
    return client.post(candidateInfoUrl('FamilyMember'), { data: payload }, config);
}

/* ─── Save: Children ─── */

export function saveChildren(payload: object[], config?: AxiosRequestConfig) {
    return client.post(candidateInfoUrl('Child'), { data: payload }, config);
}

/* ─── Save: Language ─── */

export function saveLanguageProficiency(payload: object[], config?: AxiosRequestConfig) {
    return client.post(candidateInfoUrl('Language'), { data: payload }, config);
}

export function saveSpouseLanguageProficiency(payload: object[], config?: AxiosRequestConfig) {
    return client.post('/candidate_info/SpouseLanguage', { data: payload }, config);
}

/* ─── Save: Education ─── */

export function saveEducation(payload: object[], config?: AxiosRequestConfig) {
    return client.post(candidateInfoUrl('EducationNew'), { data: payload }, config);
}

export function saveSpouseEducation(payload: object[], config?: AxiosRequestConfig) {
    return client.post('/candidate_info/SpouseEducation', { data: payload }, config);
}

/* ─── Save: Work Experience ─── */

export function saveWorkExperience(payload: object[], config?: AxiosRequestConfig) {
    return client.post(candidateInfoUrl('WorkExperienceNew'), { data: payload }, config);
}

export function saveSpouseWorkExperience(payload: object[], config?: AxiosRequestConfig) {
    return client.post('/candidate_info/SpouseWorkExperience', { data: payload }, config);
}

/* ─── Save: Job Offer ─── */

export function saveJobOffer(payload: object[], config?: AxiosRequestConfig) {
    return client.post(candidateInfoUrl('JobOffer'), { data: payload }, config);
}

/* ─── Save: Relatives Abroad ─── */

export function saveRelativesAbroad(payload: object[], config?: AxiosRequestConfig) {
    return client.post(candidateInfoUrl('RelativeAbroad'), { data: payload }, config);
}

/* ─── Save: Business ─── */

export function saveBusinessOwnership(payload: object[], config?: AxiosRequestConfig) {
    return client.post(candidateInfoUrl('Business'), { data: payload }, config);
}

/* ─── Ticket Resolve ─── */

export function resolveApplicationTicket(
    id: number,
    status: string,
    applicationId?: number,
    config?: AxiosRequestConfig
) {
    return client.put('/ticket', { ticket_id: id, status, application_id: applicationId }, config);
}
